import React, { useState, createContext, Dispatch, SetStateAction, useContext } from 'react';
import { PersonFragment } from '@graphql/generated';
import { getLocalStorage } from '@shared/core';
import { addError } from '@shared/utils/logger';

const localStorage = getLocalStorage();
const LOCAL_STORAGE_KEY = 'selectedGuests';

const getInitialSelectedPeople = () => {
  const storedValue = localStorage.getItem(LOCAL_STORAGE_KEY);

  if (!storedValue) {
    return [];
  }

  try {
    const storedIdTags = JSON.parse(storedValue);

    if (!Array.isArray(storedIdTags) || storedIdTags.length === 0) {
      return [];
    }

    return storedIdTags.map((idTag: string) => ({ personIdTag: idTag }));
  } catch (error) {
    addError(error, { context: 'PeopleContext#getInitialSelectedPeople' });
    return [];
  }
};

export type SelectedPeopleDispatch = Dispatch<SetStateAction<ReadonlyArray<Partial<PersonFragment>>>>;

export type SelectedPeople = ReadonlyArray<Partial<PersonFragment & { full_name: string; hasPhone?: boolean }>>;

export const PeopleContext = createContext<{
  selectedPeople: SelectedPeople;
  setSelectedPeople: SelectedPeopleDispatch;
  clearSelectedPeople: () => void;
}>({
  selectedPeople: [],
  setSelectedPeople() {},
  clearSelectedPeople() {},
});

export const PeopleProvider = (props: { children: React.ReactNode }) => {
  const initialSelectedPeople = getInitialSelectedPeople();
  const [selectedPeople, setSelectedPeople] = useState<ReadonlyArray<Partial<PersonFragment>>>(initialSelectedPeople);

  const clearSelectedPeople = () => {
    setSelectedPeople([]);
    localStorage.setItem(LOCAL_STORAGE_KEY, undefined);
  };

  return (
    <PeopleContext.Provider
      value={{
        selectedPeople,
        setSelectedPeople,
        clearSelectedPeople,
      }}
    >
      {props.children}
    </PeopleContext.Provider>
  );
};

export const usePeopleContext = () => {
  return useContext(PeopleContext);
};
